import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './part-modal.style.js';

import { createPart, getPartsByCompany, getAssetsByCompany } from '@cmms/api';
import { Firestore } from 'firebase/firestore';


@customElement('part-modal')
export class PartModal extends LitElement {
  @property({ type: Boolean })
  open = false;



  @property({ type: Object })
  formData: {
    name: string;
    description: string;
    stock_qty: number;
    min_qty: number;
    max_qty: number;
    reorder_level: number;
    cost: number;
    asset_id: string;
  } = {
    name: '',
    description: '',
    stock_qty: 0,
    min_qty: 0,
    max_qty: 0,
    reorder_level: 0,
    cost: 0,
    asset_id: ''
  };

  @state()
  private isLoading = false;

  @state()
  private errorMessage = '';

  @state()
  private assets: Array<{ id: string; model?: { name: string } }> = [];

  @property({ type: String }) companyId = '';
  @property({ type: Object }) firestore: Firestore = null as any;
  static styles = styles;

  connectedCallback() {
    super.connectedCallback();
    this.loadAssets();
  }




  private async loadAssets() {
    try {
      const result = await getAssetsByCompany(this.firestore, this.companyId);
      
      if (result.data) {
        this.assets = result.data;
      } else {
        this.errorMessage = result.error || 'Failed to load assets';
      }
    } catch (error) {
      this.errorMessage = 'Failed to load assets. Please try again.';
    }
  }

  private handleInputChange(e: Event, field: string) {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    let value: any = target.value;
    
    // Handle numeric fields
    if (['stock_qty', 'min_qty', 'max_qty', 'reorder_level', 'cost'].includes(field)) {
      value = value === '' ? 0 : parseFloat(value);
    }
    
    this.formData = { ...this.formData, [field]: value };
  }

  private normalizePartName(name: string): string {
    return name
      .trim() // Remove leading/trailing spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .toLowerCase(); // Convert to lowercase
  }

  private async checkDuplicatePartName(name: string): Promise<{ isDuplicate: boolean; existingName?: string }> {
    try {
      // Normalize the input name for comparison
      const normalizedInputName = this.normalizePartName(name);
      
      // Use the existing API function to get parts by company
      const result = await getPartsByCompany(this.firestore, this.companyId);
      
      if (result.error) {
        console.error('Error fetching parts for duplicate check:', result.error);
        return { isDuplicate: false }; // Allow submission if we can't check
      }
      
      if (result.data && result.data.length > 0) {
        // Check if any existing part has the same normalized name
        for (const part of result.data) {
          const existingName = part.model?.name || part.name;
          if (existingName) {
            const normalizedExistingName = this.normalizePartName(existingName);
            if (normalizedExistingName === normalizedInputName) {
              return { isDuplicate: true, existingName: existingName };
            }
          }
        }
      }
      
      return { isDuplicate: false }; // No existing parts, so no duplicates
    } catch (error) {
      console.error('Error checking for duplicate part name:', error);
      // If we can't check for duplicates, allow the submission to proceed
      // The server-side validation will catch any issues
      return { isDuplicate: false };
    }
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    
    // Check if companyId is available
    if (!this.companyId) {
      this.errorMessage = 'Company ID is required';
      return;
    }
    
    // Check if firestore is available
    if (!this.firestore) {
      this.errorMessage = 'Database connection is not available';
      return;
    }
    
    if (!this.formData.name) {
      this.errorMessage = 'Part name is required';
      return;
    }

    // Check for duplicate part name
    const duplicateCheck = await this.checkDuplicatePartName(this.formData.name);
    if (duplicateCheck.isDuplicate) {
      const normalizedExistingName = this.normalizePartName(duplicateCheck.existingName!);
      this.errorMessage = `A part with the name '${normalizedExistingName}' already exists`;
      return;
    }

    try {
      this.isLoading = true;
      this.errorMessage = '';

      // Normalize the name before saving
      const normalizedName = this.normalizePartName(this.formData.name);

      const partData = {
        name: normalizedName,
        description: this.formData.description || '',
        stock_qty: this.formData.stock_qty,
        min_qty: this.formData.min_qty,
        max_qty: this.formData.max_qty,
        reorder_level: this.formData.reorder_level,
        cost: this.formData.cost,
        asset_id: this.formData.asset_id || null,
        company_id: this.companyId
        // Don't set created_at and updated_at - let the model handle this
      };

      const result = await createPart(this.firestore, partData);

      if (!result.error) {
        // Dispatch event with the new part
        this.dispatchEvent(new CustomEvent('part-created', {
          detail: result.data,
          bubbles: true,
          composed: true
        }));
        
        // Close the modal after successful creation
        this.closeModal();
      } else {
        this.errorMessage = result.error || 'Failed to create part';
      }
    } catch (error: any) {
      console.error('Error creating part:', error);
      this.errorMessage = error.message || 'Failed to create part';
    } finally {
      this.isLoading = false;
    }
  }

  private handleCancel() {
    this.closeModal();
  }

  private closeModal() {
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }));
    this.formData = {
      name: '',
      description: '',
      stock_qty: 0,
      min_qty: 0,
      max_qty: 0,
      reorder_level: 0,
      cost: 0,
      asset_id: ''
    };
    this.errorMessage = '';
  }

  render() {
    return html`
      <sl-dialog
        label="Add a Part"
        .open=${this.open}
        @sl-hide=${this.closeModal}
      >
        <div class="modal-content">
          ${this.errorMessage ? html`
            <div class="error-message">
              <sl-icon name="exclamation-triangle" style="margin-right: 0.5rem;"></sl-icon>
              ${this.errorMessage}
            </div>
          ` : ''}

          <form @submit=${this.handleSubmit} 
            @click=${(e: Event) => e.stopPropagation()} 
            @sl-input=${(e: Event) => e.stopPropagation()} 
            @sl-change=${(e: Event) => e.stopPropagation()}
            @sl-open-change=${(e: Event) => e.stopPropagation()}
            @sl-show=${(e: Event) => e.stopPropagation()}
            @sl-hide=${(e: Event) => e.stopPropagation()}
          >
            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="name">Part Name *</label>
              <sl-input
                id="name"
                name="name"
                placeholder="Enter part name"
                value=${this.formData.name}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'name')}
                @sl-change=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
                required
                size="large"
                autocomplete="off"
              >
                <sl-icon name="box" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="description">Description</label>
              <textarea
                id="description"
                name="description"
                placeholder="Enter part description"
                .value=${this.formData.description}
                @input=${(e: Event) => this.handleInputChange(e, 'description')}
                @click=${(e: Event) => e.stopPropagation()}
                rows="3"
              ></textarea>
            </div>

            <div class="form-group" 
              @click=${(e: Event) => e.stopPropagation()}
              @sl-open-change=${(e: Event) => e.stopPropagation()}
              @sl-show=${(e: Event) => e.stopPropagation()}
              @sl-hide=${(e: Event) => e.stopPropagation()}
            >
              <label for="asset_id">Associated Asset</label>
              <sl-select
                id="asset_id"
                name="asset_id"
                value=${this.formData.asset_id}
                @sl-change=${(e: CustomEvent) => {
                  this.handleInputChange(e, 'asset_id');
                  e.stopPropagation();
                }}
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-option value="">Select an asset (optional)</sl-option>
                ${this.assets.map(asset => html`
                  <sl-option value=${asset.id}>${asset.model?.name || asset.id}</sl-option>
                `)}
              </sl-select>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="stock_qty">Stock Quantity</label>
              <sl-input
                id="stock_qty"
                name="stock_qty"
                type="number"
                placeholder="Enter current stock quantity"
                value=${this.formData.stock_qty}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'stock_qty')}
                @sl-change=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
                min="0"
              >
                <sl-icon name="layers" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="min_qty">Minimum Quantity</label>
              <sl-input
                id="min_qty"
                name="min_qty"
                type="number"
                placeholder="Enter minimum quantity"
                value=${this.formData.min_qty}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'min_qty')}
                @sl-change=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
                min="0"
              >
                <sl-icon name="arrow-down" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="max_qty">Maximum Quantity</label>
              <sl-input
                id="max_qty"
                name="max_qty"
                type="number"
                placeholder="Enter maximum quantity"
                value=${this.formData.max_qty}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'max_qty')}
                @sl-change=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
                min="0"
              >
                <sl-icon name="arrow-up" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="reorder_level">Reorder Level</label>
              <sl-input
                id="reorder_level"
                name="reorder_level"
                type="number"
                placeholder="Enter reorder level"
                value=${this.formData.reorder_level}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'reorder_level')}
                @sl-change=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
                min="0"
              >
                <sl-icon name="exclamation-triangle" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="cost">Cost</label>
              <sl-input
                id="cost"
                name="cost"
                type="number"
                placeholder="Enter part cost"
                value=${this.formData.cost}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'cost')}
                @sl-change=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
                min="0"
                step="0.01"
              >
                <sl-icon name="dollar-sign" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-footer" slot="footer">
              <sl-button
                variant="neutral"
                size="large"
                @click=${this.handleCancel}
                ?disabled=${this.isLoading}
              >
                Cancel
              </sl-button>
              <sl-button
                variant="primary"
                size="large"
                @click=${this.handleSubmit}
                ?loading=${this.isLoading}
                ?disabled=${this.isLoading}
              >
                ${this.isLoading ? 'Creating...' : 'Add'}
              </sl-button>
            </div>
          </form>
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'part-modal': PartModal;
  }
}
